import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, User, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/shop", label: "Shop" },
  { to: "/telehealth", label: "Telehealth" },
  { to: "/training", label: "Training" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 glass border-b border-brand-navy/10">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex h-24 items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
            <BrandLogo h={60} className="lg:hidden" />
            <BrandLogo h={72} className="hidden lg:block" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="font-mono-label text-[13px] uppercase tracking-[0.18em] text-brand-navy/70 hover:text-brand-blue transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="cart-button"
              onClick={() => setOpen(true)}
              className="relative p-2.5 rounded-full hover:bg-brand-mist transition-colors"
              aria-label="Cart"
            >
              <ShoppingCart className="h-5 w-5 text-brand-navy" />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-teal text-[11px] font-bold text-white"
                >
                  {count}
                </span>
              )}
            </button>

            <button
              data-testid="account-button"
              onClick={() => navigate(user ? "/account" : "/auth")}
              className="hidden md:flex p-2.5 rounded-full hover:bg-brand-mist transition-colors"
              aria-label="Account"
            >
              <User className="h-5 w-5 text-brand-navy" />
            </button>

            <Button
              data-testid="header-telehealth-cta"
              onClick={() => navigate("/telehealth")}
              className="hidden md:inline-flex rounded-full bg-brand-navy hover:bg-brand-blue text-white px-5"
            >
              Book Consult
            </Button>

            <button className="md:hidden p-2.5" onClick={() => setMobile((v) => !v)} aria-label="Menu" data-testid="mobile-menu-button">
              {mobile ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden border-t border-brand-navy/10 bg-white px-5 py-4 space-y-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setMobile(false)}
              className="block py-2.5 font-mono-label text-sm uppercase tracking-[0.15em] text-brand-navy/80"
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </Link>
          ))}
          <Link to={user ? "/account" : "/auth"} onClick={() => setMobile(false)} className="block py-2.5 font-mono-label text-sm uppercase tracking-[0.15em] text-brand-navy/80">
            {user ? "Account" : "Sign In"}
          </Link>
        </div>
      )}
    </header>
  );
};
